import type { IBallotLinkSummary, IRatingProcessStepCommitteeMatrixBallot } from '__generated__';
import { baseApi, committeeBallotTag, RTK_TAGS } from 'store/api';
import { API, APIRead, normalizeError } from 'utils/api-client';
import { buildReportMatrixDocxFileName, parseContentDispositionFileName } from './helpers';
import type {
  BallotLinkRow,
  BallotLoad,
  BallotRosterMember,
  BallotWithDetails,
  SaveBallotBody,
  SaveSharedBody,
} from './models';

/** Every ballot write can change progress, verdict, lock and the synced voting rows. */
const ALL_BALLOT_TAGS = [RTK_TAGS.CommitteeBallot];

export const committeeMatrixBallotApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /** B-01 — link, shared inputs, every ballot, roster, progress and verdict in one call. */
    getCommitteeBallot: builder.query<BallotLoad, { committeeFrameworkId: number }>({
      async queryFn({ committeeFrameworkId }) {
        try {
          const { data } = await APIRead.api.ratingProcessStepCommitteeMatrixBallotLoadDetail({
            committeeFrameworkId,
          });
          return { data: data as BallotLoad };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      providesTags: (_result, _error, arg) => [committeeBallotTag(arg.committeeFrameworkId)],
    }),

    /** F-15 — the rating process's matrix (null when it has none). */
    getBallotLinkByRatingProcess: builder.query<IBallotLinkSummary | null, { ratingProcessId: number }>({
      async queryFn({ ratingProcessId }) {
        try {
          return { data: await fetchBallotLinkByRatingProcess(ratingProcessId) };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      providesTags: ALL_BALLOT_TAGS,
    }),

    /** F-14 — the rating process status (4 = opened for update). */
    getBallotRatingProcessStatus: builder.query<number | null, { ratingProcessId: number }>({
      async queryFn({ ratingProcessId }) {
        try {
          const { data } = await APIRead.api.ratingProcessGetAllDynamicCreate({
            select: 'id,ratingProcessStatusID',
            filter: `id==${ratingProcessId}`,
          });
          const rows =
            (data as unknown as { value?: { ratingProcessStatusID: number | null }[] }).value ?? [];
          return { data: rows[0]?.ratingProcessStatusID ?? null };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
    }),

    /** Load's roster has no user / position objects — names and titles come from here. */
    getCommitteeBallotRoster: builder.query<BallotRosterMember[], { committeeId: number }>({
      async queryFn({ committeeId }) {
        try {
          const { data } = await APIRead.api.ratingProcessStepCommitteeUserGetAllDynamicCreate({
            select: [
              'id',
              'userID',
              'user.fullName as userFullName',
              'committeePositionID',
              'committeePosition.title as committeePositionTitle',
              'committeePosition.isVoting as committeePositionIsVoting',
              'votingOrder',
            ].join(','),
            filter: `ratingProcessStepCommitteeID==${committeeId}`,
            orderBy: 'votingOrder',
          });
          const rows = (data as unknown as { value?: BallotRosterMember[] }).value ?? [];
          return { data: rows };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
    }),

    /**
     * F-17 — the committee's matrix links, in chip order: the voting summary opens the ballot
     * from here, and F-19 reads the chair lock (on the primary) to freeze the roster.
     */
    getCommitteeBallotLinks: builder.query<BallotLinkRow[], { committeeId: number }>({
      async queryFn({ committeeId }) {
        try {
          const { data } =
            await APIRead.api.ratingProcessStepCommitteeRatingMatrixFrameworkGetAllDynamicCreate({
              select: 'id,ratingMatrixFrameworkTemplateID,isPrimary,chairLockedDate',
              filter: `ratingProcessStepCommitteeID==${committeeId}`,
              orderBy: 'id',
            });
          const rows = (data as unknown as { value?: BallotLinkRow[] }).value ?? [];
          return { data: rows };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      providesTags: ALL_BALLOT_TAGS,
    }),

    /**
     * F-13 — voters of the committee's primary matrix once it is locked: the lock fills their
     * main rating and outlook in the voting table, which are then read-only. Submit alone no
     * longer writes there.
     */
    getSubmittedBallotVoters: builder.query<string[], { committeeId: number }>({
      async queryFn({ committeeId }) {
        try {
          const { data } =
            await APIRead.api.ratingProcessStepCommitteeMatrixBallotGetAllDynamicCreate({
              select: 'id,userID',
              filter: [
                `committeeRatingMatrixFramework.ratingProcessStepCommitteeID==${committeeId}`,
                'committeeRatingMatrixFramework.isPrimary==true',
                'committeeRatingMatrixFramework.chairLockedDate!=null',
              ].join(' && '),
            });
          const rows = (data as unknown as { value?: { userID: string | null }[] }).value ?? [];
          return {
            data: rows
              .map((row) => row.userID?.toLowerCase() ?? null)
              .filter((id): id is string => Boolean(id)),
          };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      providesTags: ALL_BALLOT_TAGS,
    }),

    saveBallotShared: builder.mutation<
      string,
      { committeeFrameworkId: number; body: SaveSharedBody }
    >({
      async queryFn({ committeeFrameworkId, body }) {
        try {
          const { data } = await API.api.ratingProcessStepCommitteeMatrixBallotSaveSharedCreate(
            { committeeFrameworkId },
            body,
          );
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: ALL_BALLOT_TAGS,
    }),

    saveBallot: builder.mutation<
      BallotWithDetails,
      { committeeFrameworkId: number; body: SaveBallotBody }
    >({
      async queryFn({ committeeFrameworkId, body }) {
        try {
          const { data } = await API.api.ratingProcessStepCommitteeMatrixBallotSaveBallotCreate(
            { committeeFrameworkId },
            // Swagger types the body as the entity without its child rows.
            body as unknown as Partial<IRatingProcessStepCommitteeMatrixBallot>,
          );
          return { data: data as BallotWithDetails };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      // Submit follows a save in the same click — it invalidates once both are done.
    }),

    submitBallot: builder.mutation<string, { ballotId: number }>({
      async queryFn({ ballotId }) {
        try {
          const { data } = await API.api.ratingProcessStepCommitteeMatrixBallotSubmitCreate({
            id: ballotId,
          });
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: ALL_BALLOT_TAGS,
    }),

    reopenBallot: builder.mutation<string, { ballotId: number }>({
      async queryFn({ ballotId }) {
        try {
          const { data } = await API.api.ratingProcessStepCommitteeMatrixBallotReopenCreate({
            id: ballotId,
          });
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: ALL_BALLOT_TAGS,
    }),

    chairLockCommittee: builder.mutation<string, { committeeFrameworkId: number }>({
      async queryFn({ committeeFrameworkId }) {
        try {
          const { data } = await API.api.ratingProcessStepCommitteeMatrixBallotChairLockCreate({
            committeeFrameworkId,
          });
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: ALL_BALLOT_TAGS,
    }),
  }),
});

export const {
  useGetBallotLinkByRatingProcessQuery,
  useGetBallotRatingProcessStatusQuery,
  useGetCommitteeBallotQuery,
  useGetCommitteeBallotLinksQuery,
  useGetCommitteeBallotRosterQuery,
  useGetSubmittedBallotVotersQuery,
  useSaveBallotSharedMutation,
  useSaveBallotMutation,
  useSubmitBallotMutation,
  useReopenBallotMutation,
  useChairLockCommitteeMutation,
} = committeeMatrixBallotApi;

/** Refetch Load after a draft save (Submit invalidates by itself). */
export const invalidateCommitteeBallot = () =>
  committeeMatrixBallotApi.util.invalidateTags(ALL_BALLOT_TAGS);

/** F-15 — the matrix of a rating process; the endpoint answers 204 (no body) when it has none. */
export async function fetchBallotLinkByRatingProcess(
  ratingProcessId: number,
): Promise<IBallotLinkSummary | null> {
  const response = await APIRead.api.ratingProcessStepCommitteeMatrixBallotByRatingProcessDetail({
    ratingProcessId,
  });
  const data = response.data as IBallotLinkSummary | '' | null | undefined;
  return response.status === 204 || !data ? null : data;
}

/** R-35 — condensed matrix for the rating report, built by the backend from the shared values. */
export async function fetchReportMatrix(committeeFrameworkId: number) {
  const { data } = await APIRead.api.ratingProcessStepCommitteeMatrixBallotReportMatrixDetail({
    committeeFrameworkId,
  });
  return data;
}

/**
 * R-35 — the Word file needs the Bearer token, so it is fetched as a blob and saved from memory.
 * The endpoint may answer errors with 406 and an empty body; the caller shows the generic toast.
 */
export async function downloadReportMatrixDocx(committeeFrameworkId: number) {
  const response = await APIRead.api.ratingProcessStepCommitteeMatrixBallotReportMatrixDocxDetail(
    { committeeFrameworkId },
    { format: 'blob' },
  );
  const blob = response.data as unknown as Blob;
  const fileName =
    parseContentDispositionFileName(response.headers?.['content-disposition']) ??
    buildReportMatrixDocxFileName(committeeFrameworkId, new Date());

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}
